import { useEffect, useState } from 'react';

import API from 'files/api.js';

/* The article library, read from the server.

   The index used to be a hand-kept array in this file and the prose used to sit
   in /public/articles, which meant publishing a piece was a deploy of the whole
   site. Both now live on the server: the markdown in /system/articles, its
   metadata in a table built from the front matter of those files. Writing a
   piece is a file and a sync, and the site picks it up on its next load.

   The move was not really about publishing speed. The Quick Look reads an
   owner's answers on the server, and it cannot point that owner at something
   worth reading unless the server knows what has been written. The index had to
   be somewhere both halves could see.

   Everything below treats the list as data handed in, so the sort and the
   related-reading rules stay pure and only `useArticles` knows about the
   network. */

const ENDPOINT = `/api/v${API.version}/articles/`;

/* One request per page load, no matter how many components ask. Several views
   can mount wanting the index at once; the promise is shared so they queue
   behind a single fetch rather than racing. */
let cache = null;
let pending = null;

export const loadArticles = () => {

    if(cache) {
        return Promise.resolve(cache);
    }
    if(pending) {
        return pending;
    }

    pending = fetch(`${ENDPOINT}?type=articles`)
        .then(response => {
            if(!response.ok) {
                throw new Error(`${response.status}`);
            }
            return response.json();
        })
        .then(body => {
            cache = (body && body.articles) || [];
            pending = null;
            return cache;
        })
        .catch(e => {
            // cleared rather than cached, so one failure does not stick for the
            // rest of the session and the next mount gets a real attempt
            pending = null;
            throw e;
        });

    return pending;
}

/* One article's markdown, with its metadata alongside it. Both come back
   together because the page needs both to render anything at all, and asking
   twice would mean a header that appears a beat before its prose. */
export const loadArticle = slug => {
    return fetch(`${ENDPOINT}?type=content&slug=${encodeURIComponent(slug)}`)
        .then(response => {
            if(!response.ok) {
                throw new Error(`${response.status}`);
            }
            return response.json();
        });
}

/* The index, for a component that renders from it.

   `articles` is an empty list until the request lands, which is the right shape
   for a list and the wrong shape for a decision — a page that says "nothing
   written yet" while the request is still going would be lying. Hence `loading`
   as a separate answer to a separate question. */
export const useArticles = () => {

    const [articles, setArticles] = useState(cache || []);
    const [loading, setLoading] = useState(!cache);
    const [failed, setFailed] = useState(false);

    useEffect(() => {

        if(cache) {
            return;
        }

        let cancelled = false;

        loadArticles()
            .then(list => {
                if(!cancelled) {
                    setArticles(list);
                    setLoading(false);
                }
            })
            .catch(() => {
                if(!cancelled) {
                    setFailed(true);
                    setLoading(false);
                }
            });

        return () => { cancelled = true; }

    }, []);

    return { articles: articles, failed: failed, loading: loading };
}

/* Pieces worth writing next, kept here rather than in a document so the gaps in
   the library are visible from the code that consumes it. Each one is a problem
   the marketing page raises today and cannot yet point anywhere for.

   To publish one: write /system/articles/<slug>.md on the server in the same
   shape as the others — a front matter block, then # title, *dek*, ---, body,
   ---, *closing callout with a [bracketed] Quick Look line* — and run

       php -f api/v1/config/require.php -- --articles.sync

   The `situations` line in the front matter is the one that is easy to write
   carelessly and worth not: it is the sentence the Quick Look matches an owner
   against, so it describes who the piece is for rather than selling the click.
   Nothing here is rendered.

   Three pieces came off this list in September 2026 and are on the server as
   drafts: busy-season, straight-answer and twelve-locations. Each one completes
   a Common Problems row that had no article to point at. They go live by
   changing `status` in their front matter and running the sync — the home page
   picks them up on its own, because getArticleLink returns nothing for a slug
   that is not published and a real link the moment it is. */
export const PLANNED = [{
    slug: 'vendor-ceiling',
    title: 'When Your Vendor’s Roadmap Stops Being Yours',
    why: 'The vendor ceiling problem, currently borrowing the custom-software piece.'
},{
    slug: 'cost-of-ai-services',
    title: 'The Real Cost of Running AI in Your Business',
    why: 'Per-request pricing that scales with usage. Almost nobody writes this honestly.'
},{
    slug: 'free-app-in-two-days',
    title: 'That “Free App in Two Days” Ad, Explained',
    why: 'The skeptic’s moment. Names the unease an owner already feels.'
}];

export const findArticle = (articles, slug) =>
    (articles || []).find(article => article.slug === slug) || null;

/* The piece that leads the index. Rendered as its own hero above the filter, and
   left out of the list below so the page never shows it twice. */
export const featuredArticle = articles =>
    publishedArticles(articles).find(article => article.featured) || null;

/* What to read next, derived rather than curated. Same topic first, newest
   first within that, then the rest of the library to fill the row.

   Deliberately not a per-article list of related slugs: that has to be revisited
   every time a piece is published, and the one that gets forgotten is always the
   newest one — the piece most worth surfacing. Publishing a new article should
   make it appear at the foot of the existing ones on its own. */
export const relatedArticles = (articles, slug, limit = 3) => {

    const current = findArticle(articles, slug);
    const pool = publishedArticles(articles).filter(article => article.slug !== slug);

    if(!current) {
        return pool.slice(0, limit);
    }

    const sameTopic = pool.filter(article => article.topic === current.topic);
    const rest = pool.filter(article => article.topic !== current.topic);

    return [...sameTopic, ...rest].slice(0, limit);
}

/* Featured first, then newest. `featured` is how a piece gets pinned to the top
   of the index regardless of its date — set it in one file's front matter and
   clear it in the old one to change which piece leads. If two ever carry it they
   simply keep their date order between themselves rather than fighting.

   The server already returns live pieces newest first, and sorts again here
   anyway. The featured rule is the site's, and a copy of it does not belong in a
   query. */
export const publishedArticles = articles => {
    return [...(articles || [])]
        .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
            || String(b.published).localeCompare(String(a.published)));
}

// Dates are stored as plain YYYY-MM-DD and formatted without a timezone, so a
// reader west of UTC never sees an article dated the day before it published.
export const articleDate = published => {
    const [year, month, day] = String(published).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        day: 'numeric', month: 'long', year: 'numeric'
    });
}
